import './EditTeamMemberPopup.css';
import { useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useParams } from 'react-router-dom';
import { EditTeamMemberForm } from './EditTeamMemberForm';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useDeleteProjectTeamMembersMutation } from 'src/services';
import { useUpdateUserMutation } from 'src/services/usersService';
import { EditIcon } from 'src/assets/icons/editIcon';
import { Button } from 'src/components/ui/button';
import type { ProjectTeamMember } from 'src/services/projectsService';
import type { TeamMemberFormValues } from './EditTeamMemberForm';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const EditTeamMemberPopup = ({
  teamMember,
  disabled = false,
}: {
  teamMember: ProjectTeamMember;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const notification = useNotification();

  const { id } = useParams();
  const [updateUserInfoById, { isLoading: isLoadingUpdateUserInfoById }] = useUpdateUserMutation();
  const [deleteTeamMember, { isLoading: isLoadingDeleteTeamMember }] =
    useDeleteProjectTeamMembersMutation();

  const onDeleteTeamMemberClick = () => {
    deleteTeamMember({ id, teamMemberId: teamMember.id }).then((deleteTeamMemberResult) => {
      if (!('error' in deleteTeamMemberResult)) {
        notification.success('Team Member Deleted.');
        setOpen(false);
      }
    });
  };

  const submit = (formData: Partial<TeamMemberFormValues>) => {
    updateUserInfoById({ userId: teamMember.user.id, body: formData }).then(
      (updateUserInfoByIdResult) => {
        if (!('error' in updateUserInfoByIdResult)) {
          notification.success('Team Member Updated.');
          setOpen(false);
        }
      },
    );
  };

  useLoaderWhile(isLoadingDeleteTeamMember, isLoadingUpdateUserInfoById);

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <div className="edit-button">
          <EditIcon />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user information</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {teamMember && (
            <EditTeamMemberForm user={teamMember.user} onSubmit={(formData) => submit(formData)} />
          )}
        </div>
        <DialogFooter>
          <Button
            variant="pill-outline-red"
            size="pill"
            style={{ width: 'auto' }}
            onClick={() => onDeleteTeamMemberClick()}
          >
            Delete
          </Button>
          <DialogClose asChild>
            <Button variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="pill-blue"
            size="pill"
            style={{ width: 'auto' }}
            type="submit"
            form="edit-user-form"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
